import { TriangleAlert } from "lucide-react";
import type { Identifier } from "ra-core";
import { useNotify } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { AddressAlert } from "./gulaTypes";

type AddressFields = {
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  province?: string | null;
};

const formatAddress = (fields: AddressFields) =>
  [
    fields.address,
    [fields.postal_code, fields.city].filter(Boolean).join(" "),
    fields.province ? `(${fields.province})` : null,
  ]
    .filter(Boolean)
    .join(", ");

const resolveAddressAlert = async (clientId: Identifier, useAlert: boolean) => {
  const { error } = await getSupabaseClient().rpc(
    "gula_resolve_address_alert",
    { p_client_id: Number(clientId), p_use_alert: useAlert },
  );
  if (error) throw new Error(error.message);
};

/**
 * Two different delivery addresses came from Stripe: the team must pick the right
 * one (after confirming it with the client) before anything can be shipped.
 */
export const AddressAlertBox = ({
  clientId,
  current,
  alert,
  onResolved,
}: {
  clientId: Identifier;
  current: AddressFields;
  alert: AddressAlert;
  onResolved: () => void;
}) => {
  const notify = useNotify();
  const [busy, setBusy] = useState(false);

  const choose = async (useAlert: boolean) => {
    setBusy(true);
    try {
      await resolveAddressAlert(clientId, useAlert);
      notify("Dirección de envío confirmada", { type: "success" });
      onResolved();
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "No se ha podido guardar",
        { type: "error" },
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="alert"
      className="flex flex-col gap-2 rounded-lg border border-destructive/50 bg-destructive/5 p-3 text-sm"
    >
      <p className="flex items-start gap-2 font-medium text-destructive">
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
        Este cliente ha puesto dos direcciones de envío distintas en Stripe.
        Confirmad con él cuál es la buena antes de enviar.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="flex flex-1 flex-col gap-1 rounded-md border bg-background p-2">
          <span className="text-xs text-muted-foreground">En la ficha</span>
          <span>{formatAddress(current) || "Sin dirección"}</span>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => choose(false)}
          >
            Es esta
          </Button>
        </div>
        <div className="flex flex-1 flex-col gap-1 rounded-md border bg-background p-2">
          <span className="text-xs text-muted-foreground">
            Otra en Stripe{alert.source ? ` (${alert.source})` : ""}
          </span>
          <span>{formatAddress(alert)}</span>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => choose(true)}
          >
            Es esta
          </Button>
        </div>
      </div>
    </div>
  );
};
